import moment from 'moment';
import { CompanyFiscalYear } from 'api';
import { ISO_DATE_FORMAT, MOMENT_DEFAULT_DATE_FORMAT } from 'utilities';
import { DEFAULT_QUARTERS_MONTH_ENDS } from '../constants';

export const isValidCompanyFiscalYear = (fye: unknown): fye is CompanyFiscalYear =>
  typeof fye === 'object' &&
  fye !== null &&
  typeof (fye as CompanyFiscalYear).end_month === 'number' &&
  typeof (fye as CompanyFiscalYear).end_day === 'number';

/**
 * This Function calculates and return the end date of the next fiscal quarter
 *
 * @param {Object} fye - An object with two properties: end_month and end_day,
 * which represent the company's fiscal year end month and day
 *
 * @param {String} date -  A string representing the current date in YYYY-MM-DD format. If not provided, the function uses the current date.
 *
 * @return {String} The function returns a string representing the end date of the next fiscal quarter in MM/DD/YYYY format.
 * If all quarters of the current year are in the past, it returns the end date of the first quarter of the next year.
 */

const getSuggestedQuarterFiscalEndDate = (fye: CompanyFiscalYear, date: string | null = null): string => {
  // Check that fye is an object with end_month and end_day properties
  if (!isValidCompanyFiscalYear(fye)) {
    throw new Error('Invalid fiscal year end parameter. It should be an object with end_month and end_day properties (numbers).');
  }

  // Check that date is a string in YYYY-MM-DD format
  if (date && (typeof date !== 'string' || !moment(date, ISO_DATE_FORMAT).isValid())) {
    throw new Error(`Invalid date parameter. It should be a string in ${ISO_DATE_FORMAT} format.`);
  }

  const today = date ? moment(date) : moment();
  const currentYear = today.year();
  const currentFye = moment(`${currentYear}-${fye.end_month}-${fye.end_day}`, ISO_DATE_FORMAT);

  const currentQuarters = DEFAULT_QUARTERS_MONTH_ENDS
    .map(months => {
      const quarterEnd = currentFye.isSameOrAfter(today)
        ? currentFye.clone().add(months, 'months').year(today.year())
        : currentFye.clone().add(months, 'months').endOf('month');
      return quarterEnd.format(MOMENT_DEFAULT_DATE_FORMAT);
    })
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

  const result = currentQuarters.find(quarter => today.isSameOrBefore(moment(quarter, MOMENT_DEFAULT_DATE_FORMAT)));
  if (!result) {
    throw new Error('No valid quarter end date found.');
  }
  return result;
};

export default getSuggestedQuarterFiscalEndDate;
